import {
  AmbientLight,
  type Box3,
  OrthographicCamera,
  Sphere,
  Vector3
} from "three";

import type { Game } from "src/api";

import { CameraContext } from "../../contexts/camera";
import { Lighting } from "../../render/Lighting";
import { Renderer } from "../../render/Renderer";
import { BoardCamera, BoardCameraAPI } from "../../render/camera/BoardCamera";
import { WorldObjectPicker } from "../../render/input/WorldObjectPicker";
import { World } from "../../render/world/World";
import { kCentralInteractions } from "../../singletons/CentralInteraction";
import {
  BOARD_LIGHTING,
  CAMERA_DISTANCE,
  CAMERA_PITCH_DEGREES,
  CAMERA_YAW_DEGREES,
  VIEW_HEIGHT
} from "../gameConstants";
import { BoardUpdateQueue } from "./updates/BoardUpdateQueue";
import { boundsToBox } from "./utilities/boardCoordinates";
import { BoardTiles } from "./worldObjects/BoardTiles";
import { BoardPieces } from "./worldObjects/pieces/BoardPieces";
import { BoardResourceDeposits } from "./worldObjects/resourceDeposits/BoardResourceDeposits";
import { BoardScrapHeaps } from "./worldObjects/scrapHeaps/BoardScrapHeaps";
import { BoardStructures } from "./worldObjects/structures/BoardStructures";

export class BoardViewport {
  private readonly camera = new OrthographicCamera(
    0,
    0,
    VIEW_HEIGHT / 2,
    -VIEW_HEIGHT / 2,
    0.1,
    100
  );
  private readonly world = new World();
  private readonly renderer: Renderer;
  private readonly cameraAPI: BoardCameraAPI;
  private readonly lighting: Lighting;
  private readonly worldObjectPicker: WorldObjectPicker;
  private readonly boardUpdateQueue = new BoardUpdateQueue(this.world);
  private unsubscribeFromGame: (() => void) | null = null;
  private cameraCtx?: CameraContext;

  constructor(container: HTMLElement, background: string, game: Game) {
    this.world.setBackgroundColor(background);

    this.renderer = new Renderer(container, this.world, this.camera);
    this.world.add(new BoardTiles(game));
    this.world.add(new BoardStructures(this.world));
    this.world.add(new BoardScrapHeaps(this.world));
    this.world.add(new BoardResourceDeposits(this.world));
    this.world.add(new BoardPieces(this.world));

    // Add ambient light for physical materials.
    this.world.scene.add(new AmbientLight(0x88aaff, 0.3));

    this.lighting = new Lighting(this.world, this.renderer, BOARD_LIGHTING);

    this.cameraAPI = new BoardCamera(this.camera, this.renderer.canvas);

    this.worldObjectPicker = new WorldObjectPicker(
      this.renderer.canvas,
      this.camera,
      this.world
    );

    this.renderer.onFrame((dt) => this.cameraAPI.update(dt));
    this.renderer.onFrame(() => this.startNextBoardUpdate());

    if (game.session) kCentralInteractions.setSession(game.session);

    const facts = game.board.facts();
    this.boardUpdateQueue.add({ delta: { kind: "load" }, facts });
    kCentralInteractions.setBoardFacts(game.board.facts());

    this.unsubscribeFromGame = game.on("turn", (delta) => {
      this.boardUpdateQueue.add({ delta, facts: game.board.facts() });
      if (game.session) kCentralInteractions.setSession(game.session);
      kCentralInteractions.setBoardFacts(game.board.facts());
    });

    this.aimCameraAt(boundsToBox(facts.framed));
  }

  setCameraContext(ctx: CameraContext) {
    this.cameraCtx = ctx;
    ctx.boardCamera = this.cameraAPI;
    this.cameraAPI.controls.addEventListener("update", () =>
      this.publishCameraContext()
    );
    this.publishCameraContext();
    return this;
  }

  dispose() {
    this.unsubscribeFromGame?.();
    this.cameraAPI.controls.dispose();
    this.worldObjectPicker.dispose();
    this.lighting.dispose();
    this.world.dispose();
    this.renderer.dispose();
  }

  private startNextBoardUpdate() {
    const boardUpdate = this.boardUpdateQueue.startNextUpdateIfReady();
    if (boardUpdate)
      this.lighting.setFocusArea(boundsToBox(boardUpdate.facts.bounds));
  }

  private aimCameraAt(box: Box3) {
    const sphere = box.getBoundingSphere(new Sphere());
    const polarAngle = ((90 - CAMERA_PITCH_DEGREES) * Math.PI) / 180;
    const yaw = (CAMERA_YAW_DEGREES * Math.PI) / 180;

    this.cameraAPI.controls.fitToBox(box, false);
    this.camera.position
      .copy(sphere.center)
      .add(
        new Vector3().setFromSphericalCoords(CAMERA_DISTANCE, polarAngle, yaw)
      );
    this.camera.zoom = VIEW_HEIGHT / (sphere.radius * 2);
    this.camera.updateProjectionMatrix();
    this.publishCameraContext();
  }

  private publishCameraContext() {
    if (!this.cameraCtx) return;
    this.cameraCtx.camera = this.camera;
    this.cameraCtx.centerPoint = new Vector3();
    this.cameraAPI.controls.getTarget(this.cameraCtx.centerPoint);
    this.cameraCtx.worldRotation = this.camera.quaternion.clone();
    this.cameraCtx.events.emit("update");
  }
}
